import { Chart, ChartConfiguration, TooltipItem } from 'chart.js';
import moment from 'moment-timezone';
import { PerformanceInterval } from '../../../../models/performance-chart.model';
import { ChipModel } from '../../../../models/chip.model';
import { LocaleTimeUtils } from '../../../shared/utils/locale-time.utils';

export interface ChartDatasets {
  targetData: { x: number; y: number }[];
  baselineData: { x: number; y: number }[];
  demandData: { x: number; y: number }[];
}

export abstract class PerformanceIntervalConfig {
  // Prevent instantiation or inheritance of this class
  private constructor() {}
  /**
   * Creates the Chart.js configuration for the performance interval chart
   */
  static getChartConfig(datasets: ChartDatasets, chipsInfo: ChipModel[], locale: string, timeZone = 'UTC', boundaries: number[] = []): ChartConfiguration<'line'> {
    return {
      type: 'line',
      data: {
        datasets: [
          {
            label: chipsInfo[0].label,
            data: datasets.baselineData,
            borderColor: chipsInfo[0].color,
            borderWidth: 2,
            fill: false,
            tension: 0.25,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: chipsInfo[0].color,
            spanGaps: false,
          },
          {
            label: chipsInfo[1].label,
            data: datasets.demandData,
            borderColor: chipsInfo[1].color,
            borderWidth: 2,
            fill: false,
            tension: 0.25,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: chipsInfo[1].color,
            order: 1,
          },
          {
            label: chipsInfo[2].label,
            data: datasets.targetData,
            borderColor: chipsInfo[2].color,
            backgroundColor: `${chipsInfo[2].color}33`,
            borderWidth: 2,
            fill: 'origin',
            tension: 0.25,
            pointRadius: 0,
            order: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        elements: {
          line: {
            tension: 0.8 // 0.4 is the standard for smooth curves
          }
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: this.getTooltipConfig(locale, timeZone),
        },
        scales: this.getScalesConfig(timeZone, boundaries),
      },
    };
  }

  /**
   * Tooltip configuration
   */
  private static getTooltipConfig(locale: string, timeZone: string) {
    return {
      enabled: true,
      backgroundColor: '#fff',
      titleColor: '#000',
      bodyColor: '#000',
      borderColor: '#ddd',
      borderWidth: 1,
      padding: 25,
      boxPadding: 6,
      bodySpacing: 10,
      titleMarginBottom: 10,
      usePointStyle: true,
      bodyFont: {
        family: "'Roobert ENEL', sans-serif",
      },
      cornerRadius: 16,

      callbacks: {
        title: (context: TooltipItem<'line'>[]) => {
          const date = moment.tz(context[0].parsed.x, timeZone).locale(locale);
          const dayName = date.format('ddd');
          const datePart = LocaleTimeUtils.formatShortDate(date.toDate(), locale, { timeZone });
          const startTime = date.format('HH:mm z');

          return `${dayName} ${datePart}     ${startTime}`;
        },
        label: (context: TooltipItem<'line'>) => {
          if (context.dataset.label === 'Dispatch Period') {
            return '';
          }
          const value = context.parsed.y !== null ? context.parsed.y.toFixed(2) : 'N/A';
          const label = context.dataset.label || '';

          return context.dataset.label !== 'Target'
            ? `${label}\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t${value} kW`
            : `${label}\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t${value} kW`;
        },
        labelColor: (context: TooltipItem<'line'>) => {
          return {
            borderColor: context.dataset.borderColor as string,
            backgroundColor: context.dataset.borderColor as string,
            borderWidth: 2,
            borderRadius: 2,
          };
        },
      },
    };
  }

  /**
   * Scales (axes) configuration
   */
  private static getScalesConfig(timeZone: string, boundaries: number[]) {
    return {
      x: {
        type: 'time' as const,
        time: {
          unit: 'hour' as const,
          displayFormats: {
            hour: 'HH:mm',
          },
          tooltipFormat: 'MMM D, YYYY h:mm A',
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1,
        },
        border: {},
        title: {},
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: (value: string | number) => moment.tz(value, timeZone).format('HH:mm'),
          color: '#000',
          font: {
            size: 12,
            weight: 400
          },
        },
        afterBuildTicks: (scale: { ticks: { value: number }[] }) => {
          // Drop regular ticks whose label would overlap a boundary label
          const minGapMs = 30 * 60 * 1000;
          const regular = scale.ticks
            .map(tick => tick.value)
            .filter(value => !boundaries.some(boundary => Math.abs(boundary - value) < minGapMs));
          const all = new Set([...regular, ...boundaries]);
          scale.ticks = [...all].sort((first, second) => first - second).map(value => ({ value }));
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Electricity Demand (kW)',
          color: '#000',
          font: {
            size: 12,
            weight: 400
          },
        },
        grid: {
          display: true,
          drawOnChartArea: true,
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1,
        },
        border: {
          display: false,
        },
        ticks: {
          stepSize: 25,
          color: '#000',
          font: {
            weight: 400,
          },
        },
      },
    };
  }

  /**
   * Transform performance data to chart datasets
   */
  static transformData(intervals: PerformanceInterval[]): ChartDatasets {
    return {
      targetData: intervals.map(interval => ({
        x: moment(interval.interval_dttm).valueOf(),
        y: interval.dispatch_target_kw,
      })),
      baselineData: intervals.map(interval => ({
        x: moment(interval.interval_dttm).valueOf(),
        y: interval.baseline_kw,
      })),
      demandData: intervals.map(interval => ({
        x: moment(interval.interval_dttm).valueOf(),
        y: interval.demand_kw,
      })),
    };
  }

  static verticalHoverLine() {
    return {
      id: 'verticalHoverLine',
      beforeDatasetsDraw(chart: Chart) {
        const { ctx } = chart;

        const chartArea = chart.chartArea;
        if (!chartArea) {
          return;
        }

        const { top, bottom } = chartArea;

        const activeElements = chart.getActiveElements
          ? chart.getActiveElements()
          : [];

        if (!activeElements || activeElements.length === 0) {
          return;
        }

        const x = activeElements[0].element.x;

        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = '#C2CDDD';
        ctx.lineWidth = 2;
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
        ctx.restore();
      },
    }
  }

  static annotationBoxPlugin() {
    return {
      id: 'annotationBoxPlugin',
      beforeDraw(chart: Chart) {
        const { ctx, chartArea } = chart as any;
        const xScale = (chart as any).scales?.x;
        if (!xScale || !chartArea) return;

        const annotations = chart.options?.plugins?.annotation?.annotations || {};
        Object.values(annotations).forEach((annotation: any) => {
          if (!annotation || annotation.type !== 'box') return;

          const xMinVal = annotation.xMin ? new Date(annotation.xMin).getTime() : null;
          const xMaxVal = annotation.xMax ? new Date(annotation.xMax).getTime() : null;
          if (xMinVal == null || xMaxVal == null) return;

          let xMin = xScale.getPixelForValue
            ? xScale.getPixelForValue(xMinVal)
            : xScale.getPixelForValue(xMinVal);
          let xMax = xScale.getPixelForValue
            ? xScale.getPixelForValue(xMaxVal)
            : xScale.getPixelForValue(xMaxVal);

          // Handle case when chart has no data and getPixelForValue returns NaN
          // In this case, use the scale's min/max and calculate pixel position proportionally
          if (isNaN(xMin) || isNaN(xMax)) {
            const scaleMin = xScale.min;
            const scaleMax = xScale.max;

            if (scaleMin != null && scaleMax != null) {
              const scaleDuration = scaleMax - scaleMin;
              const chartWidth = chartArea.right - chartArea.left;

              // Calculate proportional pixel positions based on time values
              xMin = chartArea.left + ((xMinVal - scaleMin) / scaleDuration) * chartWidth;
              xMax = chartArea.left + ((xMaxVal - scaleMin) / scaleDuration) * chartWidth;
            } else {
              return; // Can't render without valid scale bounds
            }
          }

          // Clamp pixel values to chart area bounds to handle annotations that extend beyond chart boundaries
          const clampedXMin = Math.max(xMin, chartArea.left);
          const clampedXMax = Math.min(xMax, chartArea.right);

          if (isNaN(clampedXMin) || isNaN(clampedXMax) || clampedXMin >= clampedXMax) return;

          ctx.save();
          ctx.fillStyle = annotation.backgroundColor || 'rgba(0,0,0,0.05)';
          const left = Math.min(clampedXMin, clampedXMax);
          const width = Math.abs(clampedXMax - clampedXMin);
          ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
          ctx.restore();
        });
      }
    }
  };
}


